import React, { useState, useCallback, useRef } from 'react';

/**
 * Hook that returns [confirm, ConfirmModal].
 * Usage:
 *   const [confirm, ConfirmDialog] = useConfirm();
 *   const ok = await confirm({ title: '...', message: '...' });
 *   // render <ConfirmDialog /> somewhere in JSX
 */
export function useConfirm() {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title, message, confirmLabel = 'Confirm', danger = false }) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ title, message, confirmLabel, danger });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    setState(null);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    setState(null);
  }, []);

  const ConfirmDialog = useCallback(() => {
    if (!state) return null;
    return (
      <div className="mol-confirm-overlay" onClick={handleCancel}>
        <div className="mol-confirm" onClick={(e) => e.stopPropagation()}>
          <h3 className="mol-confirm__title">{state.title}</h3>
          <p className="mol-confirm__message">{state.message}</p>
          <div className="mol-confirm__actions">
            <button className="mol-btn mol-btn--secondary mol-btn--md" onClick={handleCancel}>
              Cancel
            </button>
            <button
              className={`mol-btn mol-btn--md ${state.danger ? 'mol-btn--danger' : 'mol-btn--primary'}`}
              onClick={handleConfirm}
            >
              {state.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }, [state, handleConfirm, handleCancel]);

  return [confirm, ConfirmDialog];
}
